// <i80-coverage-map>: our own component (not in Helios), made for zPlan. Days across and
// rows (e.g. day, evening, night) down; each cell shows how many are planned against how
// many are needed. The component counts nothing itself: the server sets need, have and
// status on each <i80-coverage>, so the rules stay on the server.
import { I80Element, define, h } from "../base.js";
import css from "./coverage-map.css";

const STATUSES = ["ok", "soft", "hard", "over", "none"];

function parse(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s ?? "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** ISO 8601 week number. */
function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  return Math.ceil(((t - Date.UTC(t.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
}

function ensureStyles() {
  if (document.querySelector("style[data-i80-coverage-map]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-i80-coverage-map", "");
  style.textContent = css;
  document.head.append(style);
}

/**
 * <i80-coverage-map start="2026-10-05" days="28" locale="da-DK" label="Dækning">
 *   <i80-coverage-row key="day" label="Dag" detail="06–14"></i80-coverage-row>
 *   <i80-coverage date="2026-10-05" row="day" need="3" have="2" status="hard" note="1 short 07–09"></i80-coverage>
 * </i80-coverage-map>
 *
 * One tab stop; the arrow keys move between cells, Enter or a click selects. Fires
 * i80-coverage-select ({date, row, need, have, status, note, el}); a day heading selects
 * the whole day (row is null).
 */
export class I80CoverageMap extends I80Element {
  static get observedAttributes() {
    return ["start", "days", "selected", "selected-row", "today", "locale", "label", "label-week"];
  }

  setup() {
    ensureStyles();
    this.querySelector(':scope > [data-i80-part="coverage-map"]')?.remove();
    this._root = h("div", { class: "i80-coverage-map", "data-i80-part": "coverage-map" });
    this.append(this._root);
    this._root.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-i80-cell], [data-i80-day]");
      if (btn) this._choose(btn);
    });
    this._root.addEventListener("keydown", (e) => this._key(e));
    this._observer = new MutationObserver((records) => {
      const relevant = (r) => {
        if (r.target === this) return r.type === "childList" && [...r.addedNodes, ...r.removedNodes].some((n) => n !== this._root);
        return !this._root.contains(r.target);
      };
      if (records.some(relevant)) this.update();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._observer?.disconnect();
  }

  get locale() {
    return this.getAttribute("locale") || this.closest("[lang]")?.getAttribute("lang") || undefined;
  }

  get start() {
    return parse(this.getAttribute("start")) ?? parse(iso(new Date()));
  }

  get days() {
    const n = parseInt(this.getAttribute("days") ?? "28", 10);
    return n > 0 && n <= 366 ? n : 28;
  }

  get rows() {
    return [...this.querySelectorAll(":scope > i80-coverage-row")].map((el) => ({
      key: el.getAttribute("key") ?? "", label: el.getAttribute("label") ?? el.getAttribute("key") ?? "", detail: el.getAttribute("detail"),
    }));
  }

  cell(date, row) {
    const el = [...this.querySelectorAll(":scope > i80-coverage")].find((c) => c.getAttribute("date") === date && (c.getAttribute("row") ?? "") === row);
    if (!el) return null;
    const num = (name) => {
      const v = parseInt(el.getAttribute(name) ?? "", 10);
      return Number.isFinite(v) ? v : null;
    };
    const status = el.getAttribute("status");
    return { el, need: num("need"), have: num("have"), status: STATUSES.includes(status) ? status : "ok", note: el.getAttribute("note") };
  }

  update() {
    this._observer?.disconnect();
    try {
      this._render();
    } finally {
      if (this.isConnected) this._observer?.observe(this, { childList: true, subtree: true, attributes: true, characterData: true });
    }
  }

  _render() {
    const locale = this.locale;
    const start = this.start;
    const days = Array.from({ length: this.days }, (_, i) => addDays(start, i));
    const rows = this.rows;
    const selected = this.getAttribute("selected");
    const selectedRow = this.getAttribute("selected-row");
    const today = this.getAttribute("today");
    const weekWord = this.getAttribute("label-week") ?? "Week";
    const focusKey = this._focusKey ?? (selected ? `${selected}|${selectedRow ?? rows[0]?.key ?? ""}` : `${iso(start)}|${rows[0]?.key ?? ""}`);
    const hadFocus = this._root.contains(document.activeElement);

    const long = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" });
    const wd = new Intl.DateTimeFormat(locale, { weekday: "narrow" });
    const weekend = (d) => d.getDay() === 0 || d.getDay() === 6;
    const dayClass = (d, base) => [
      base,
      weekend(d) && `${base}--weekend`,
      d.getDay() === 1 && `${base}--week-start`,
      iso(d) === selected && `${base}--selected`,
      iso(d) === today && `${base}--today`,
    ];

    // Week groups: a new group every Monday (and at the first day).
    const weeks = [];
    for (const d of days) {
      if (!weeks.length || d.getDay() === 1) weeks.push({ first: d, count: 0 });
      weeks[weeks.length - 1].count += 1;
    }

    const head = h("thead", {},
      h("tr", { class: "i80-coverage-map__weeks" },
        h("td", {}),
        weeks.map((w) => h("th", { scope: "colgroup", colspan: w.count, class: "i80-coverage-map__week" }, `${weekWord} ${isoWeek(w.first)}`))),
      h("tr", {},
        h("td", {}),
        days.map((d) => h("th", { scope: "col", class: dayClass(d, "i80-coverage-map__day") },
          h("button", {
            type: "button", class: "i80-coverage-map__day-button", "data-i80-day": iso(d), tabindex: "-1",
            "aria-label": long.format(d), "aria-pressed": String(iso(d) === selected && !selectedRow),
          },
          h("span", { class: "i80-coverage-map__weekday", "aria-hidden": "true" }, wd.format(d)),
          h("span", { class: "i80-coverage-map__date", "aria-hidden": "true" }, d.getDate()))))));

    const body = h("tbody", {}, rows.map((row) => h("tr", {},
      h("th", { scope: "row", class: "i80-coverage-map__row" },
        h("span", { class: "i80-coverage-map__row-label" }, row.label),
        row.detail && h("span", { class: "i80-coverage-map__row-detail" }, row.detail)),
      days.map((d) => {
        const key = iso(d);
        const c = this.cell(key, row.key);
        const status = c?.status ?? "none";
        const text = c && c.need !== null ? `${c.have ?? 0}/${c.need}` : "";
        const label = [long.format(d), row.label, c && c.need !== null ? `${c.have ?? 0} / ${c.need}` : null, c?.note].filter(Boolean).join(", ");
        const isSelected = key === selected && row.key === selectedRow;
        return h("td", { class: [...dayClass(d, "i80-coverage-map__cell"), `i80-coverage-map__cell--${status}`] },
          h("button", {
            type: "button", class: "i80-coverage-map__button", "data-i80-cell": `${key}|${row.key}`,
            tabindex: `${key}|${row.key}` === focusKey ? "0" : "-1",
            "aria-label": label, "aria-pressed": String(isSelected), title: c?.note ?? null,
          }, text));
      }))));

    const table = h("table", { class: "i80-coverage-map__grid", "aria-label": this.getAttribute("label") ?? "Coverage" }, head, body);
    this._root.replaceChildren(h("div", { class: "i80-coverage-map__scroll" }, table));
    if (!this._root.querySelector('[data-i80-cell][tabindex="0"]')) this._root.querySelector("[data-i80-cell]")?.setAttribute("tabindex", "0");
    if (hadFocus && this._focusKey) this._root.querySelector(`[data-i80-cell="${this._focusKey}"]`)?.focus();
  }

  _choose(btn) {
    const cell = btn.getAttribute("data-i80-cell");
    const [date, row] = cell ? cell.split("|") : [btn.getAttribute("data-i80-day"), null];
    this.select(date, row);
  }

  /** Select a day (row null) or one cell, and fire i80-coverage-select. */
  select(date, row = null) {
    if (row !== null) this._focusKey = `${date}|${row}`;
    this.setAttribute("selected", date);
    if (row === null) this.removeAttribute("selected-row");
    else this.setAttribute("selected-row", row);
    const c = row === null ? null : this.cell(date, row);
    this.dispatchEvent(new CustomEvent("i80-coverage-select", {
      bubbles: true,
      detail: { date, row, need: c?.need ?? null, have: c?.have ?? null, status: c?.status ?? null, note: c?.note ?? null, el: c?.el ?? null },
    }));
  }

  _key(e) {
    const btn = e.target.closest?.("[data-i80-cell]");
    if (!btn) return;
    const [date, row] = btn.getAttribute("data-i80-cell").split("|");
    const rows = this.rows.map((r) => r.key);
    const d = parse(date);
    const first = this.start;
    const last = addDays(first, this.days - 1);
    let to = null;
    const r = rows.indexOf(row);
    if (e.key === "ArrowRight") to = [addDays(d, 1), row];
    else if (e.key === "ArrowLeft") to = [addDays(d, -1), row];
    else if (e.key === "ArrowDown") to = [d, rows[Math.min(r + 1, rows.length - 1)]];
    else if (e.key === "ArrowUp") to = [d, rows[Math.max(r - 1, 0)]];
    else if (e.key === "Home") to = [first, row];
    else if (e.key === "End") to = [last, row];
    if (!to) return;
    e.preventDefault();
    if (to[0] < first || to[0] > last) return;
    this._focusKey = `${iso(to[0])}|${to[1]}`;
    for (const b of this._root.querySelectorAll('[data-i80-cell][tabindex="0"]')) b.setAttribute("tabindex", "-1");
    const next = this._root.querySelector(`[data-i80-cell="${this._focusKey}"]`);
    next?.setAttribute("tabindex", "0");
    next?.focus();
  }
}

define("i80-coverage-map", I80CoverageMap);
